/**
 * IANA time zones for a picker. The list is the runtime's own (`Intl.supportedValuesOf`), which is the zone names a
 * browser can show a time in; the database checks the chosen name against `pg_timezone_names` again when it is saved.
 */

export interface TimeZoneOption {
  readonly value: string;
  readonly label: string;
}

/** Every zone the runtime knows, plus UTC and any extra names (such as the zone already saved), sorted and unique. */
export function supportedTimeZones(extra: readonly string[] = []): string[] {
  let zones: string[] = [];
  try {
    zones = Intl.supportedValuesOf("timeZone");
  } catch {
    zones = [];
  }
  return [...new Set([...zones, "UTC", ...extra.filter((z) => z !== "")])].sort((a, b) =>
    a.localeCompare(b),
  );
}

/** A zone name as people read it: "America/New_York" becomes "America/New York". */
export function friendlyZoneName(zone: string): string {
  return zone.replace(/_/g, " ");
}

/** The clock time in a zone at an instant ("10:42 AM"), or null when the runtime does not know the zone. */
export function timeInZone(zone: string, at: Date, locale = "en-US"): string | null {
  try {
    return new Intl.DateTimeFormat(locale, {
      timeZone: zone,
      hour: "numeric",
      minute: "2-digit",
    }).format(at);
  } catch {
    return null;
  }
}

/** "America/New York — 10:42 AM now". */
export function timeZoneLabel(zone: string, at: Date, locale = "en-US"): string {
  const time = timeInZone(zone, at, locale);
  return time === null ? friendlyZoneName(zone) : `${friendlyZoneName(zone)} — ${time} now`;
}

/** Options for a searchable select, each label carrying the zone's time at `at`. */
export function timeZoneOptions(
  at: Date,
  extra: readonly string[] = [],
  locale = "en-US",
): TimeZoneOption[] {
  return supportedTimeZones(extra).map((zone) => ({
    value: zone,
    label: timeZoneLabel(zone, at, locale),
  }));
}
